import React from 'react'

import { IconComponent } from '@octane/spark'
import { useBrandConfig } from 'contexts/BrandConfigContextProvider'
import useGoogleTagManager from 'hooks/useGoogleTagManager'

import {
    NavbarItemLink,
    NavbarItemStyled,
    NavbarNavLabel,
    NotificationCounter,
} from './NavbarItem.styled'

const ICON_SIZE = 21

export enum UrlMatchType {
    Exact,
    StartsWith,
}

export interface NavbarItemProps {
    id: string
    href: string
    text: string
    icon: IconComponent
    urlMatchType: UrlMatchType
    notificationCount?: number | string | null
    shouldShowNotification?: boolean
    newTab?: boolean
    onClick?: () => void
    wider?: boolean
}

const isHrefActive = (href: string, urlMatchType: UrlMatchType) => {
    switch (urlMatchType) {
        case UrlMatchType.Exact:
            return href === window.location.hash

        case UrlMatchType.StartsWith:
            return window.location.hash.startsWith(href)

        default:
            return false
    }
}

export default function NavbarItem({
    id,
    href,
    icon: NavIcon,
    text,
    urlMatchType,
    notificationCount,
    shouldShowNotification = true,
    newTab,
    onClick,
    wider,
}: NavbarItemProps): JSX.Element {
    const isActive = isHrefActive(href, urlMatchType)
    const googleTagManager = useGoogleTagManager()
    const { navbarForceWhite } = useBrandConfig()
    const onLeadsPage = window.location.hash === '#/leads'

    return (
        <NavbarItemStyled
            role="menuitem"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    onClick?.()
                }
            }}
            onClick={onClick}
            isWider={!!wider}
            isActive={isActive}
            isNavbarWhite={navbarForceWhite}
        >
            <NavbarItemLink
                href={href}
                id={id}
                onClick={() => {
                    googleTagManager.trackNavbarEvent(text, href)
                }}
                target={newTab ? '_blank' : '_self'}
                rel="noopener noreferrer"
                isActive={isActive}
                data-is-active={isActive ? 'true' : 'false'}
            >
                <div>
                    <NavIcon width={ICON_SIZE} height={ICON_SIZE} />
                    {!onLeadsPage &&
                        shouldShowNotification &&
                        notificationCount && (
                            <NotificationCounter>
                                {notificationCount}
                            </NotificationCounter>
                        )}
                </div>
                <NavbarNavLabel>{text}</NavbarNavLabel>
            </NavbarItemLink>
        </NavbarItemStyled>
    )
}

NavbarItem.UrlMatchType = UrlMatchType
